/**
 * AISparkle
 *
 * Visual indicator for AI-generated or AI-assisted content. A 4-pointed star
 * with subtle inner glow, communicating intelligence and capability without
 * being overly decorative.
 *
 * Dependencies: @/lib/utils (cn)
 *
 * Copy this file to src/components/ui/AISparkle.tsx
 */

import React from "react"
import { cn } from "@/lib/utils"

type AISparkleSize = "sm" | "md" | "lg"

const SIZE_MAP: Record<AISparkleSize, number> = {
  sm: 16,
  md: 20,
  lg: 24,
}

interface AISparkleProps {
  size?: AISparkleSize
  className?: string
  /** Optional animated pulse effect */
  animate?: boolean
}

export function AISparkle({
  size = "md",
  className,
  animate = false,
}: AISparkleProps) {
  const dimension = SIZE_MAP[size]

  return (
    <svg
      width={dimension}
      height={dimension}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn(
        "text-primary",
        animate && "animate-pulse",
        className
      )}
      aria-hidden="true"
    >
      {/* Outer glow (subtle) */}
      <defs>
        <filter id="sparkle-glow">
          <feGaussianBlur stdDeviation="0.8" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* 4-pointed star geometry — classic sparkle shape */}
      {/* Center circle */}
      <circle cx="12" cy="12" r="2" fill="currentColor" />

      {/* Vertical points */}
      <path
        d="M 12 3 L 13 9 L 12 12 L 11 9 Z"
        fill="currentColor"
        opacity="0.9"
      />
      <path
        d="M 12 21 L 13 15 L 12 12 L 11 15 Z"
        fill="currentColor"
        opacity="0.9"
      />

      {/* Horizontal points */}
      <path
        d="M 3 12 L 9 13 L 12 12 L 9 11 Z"
        fill="currentColor"
        opacity="0.9"
      />
      <path
        d="M 21 12 L 15 13 L 12 12 L 15 11 Z"
        fill="currentColor"
        opacity="0.9"
      />

      {/* Diagonal accent points (subtle, 50% opacity) */}
      <path
        d="M 5.3 5.3 L 8.5 8.5 L 12 12 L 8.5 8.5 Z"
        fill="currentColor"
        opacity="0.5"
      />
      <path
        d="M 18.7 18.7 L 15.5 15.5 L 12 12 L 15.5 15.5 Z"
        fill="currentColor"
        opacity="0.5"
      />
      <path
        d="M 18.7 5.3 L 15.5 8.5 L 12 12 L 15.5 8.5 Z"
        fill="currentColor"
        opacity="0.5"
      />
      <path
        d="M 5.3 18.7 L 8.5 15.5 L 12 12 L 8.5 15.5 Z"
        fill="currentColor"
        opacity="0.5"
      />
    </svg>
  )
}

/**
 * RULES
 *
 * When to use:
 *   - Mark AI-generated content (responses, suggestions, auto-fills)
 *   - Indicate AI assistance in actions or workflows
 *   - Signal intelligent features or recommendations
 *   - Badge for copilot-assisted UI elements
 *
 * When NOT to use:
 *   - Generic decorative icons (use lucide-react)
 *   - Loading or processing spinners (use Spinner)
 *   - Animated background elements without context
 *
 * Key rules:
 *   - Uses text-primary token — no custom colors
 *   - Sizes: sm (16px), md (20px), lg (24px)
 *   - Optional animate prop adds gentle pulse for attention
 *   - aria-hidden because it's decorative; pair with explicit text label
 *   - Center circle + 4 cardinal points + 4 diagonal accents (subtle)
 *
 * USAGE
 *   <AISparkle size="md" />
 *   <AISparkle size="lg" animate />
 *   <button>
 *     <AISparkle className="mr-2" />
 *     Generate with AI
 *   </button>
 */
